import React, { useState, useEffect, useLayoutEffect, Fragment, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useBoardStore } from '../../../../stores/boardStore';
import { boardService } from '../../../../lib/api/boards';
import { validateFormula, CATEGORIES, ALL_CATEGORIES, OPERATORS, ValidationError } from '../../../../lib/formulaValidator';
import {
    channelToken,
    journalToken,
    entityToken,
    parseTokenId,
    isCategory,
    isAllKeyword,
} from '../../../../lib/formulaTokens';
import { tokenize as parserTokenize, evaluate as parserEvaluate } from '../../../../lib/formulaParser';
import { FormulaEditor, FormulaEditorRef } from './FormulaEditor';

interface BoardSpecModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack?: () => void;
    boardId: string;
    spec?: any;
}

export const BoardSpecModal: React.FC<BoardSpecModalProps> = ({
    isOpen,
    onClose,
    onBack,
    boardId,
    spec
}) => {
    const { t } = useTranslation('common');
    const queryClient = useQueryClient();
    const { channels, journals, entities, setSpecs, specs, finance, currencySettings } = useBoardStore();

    // Pseudo-channel source entities — registered as top-level tokens so
    // formulas can address a pseudo-channel via ent_<sourceEntityId>.
    const pseudoSourceEntityIds = useMemo(() => {
        const ids = new Set<string>();
        channels.forEach((c) => {
            if (c.isPseudoChannel && c.sourceEntityId) ids.add(c.sourceEntityId);
        });
        return ids;
    }, [channels]);

    /**
     * Build validation context using ID-based tokens.
     * Maps the entity/channel/journal hierarchy into a token tree.
     */
    const validationContext = useMemo(() => {
        const roots: string[] = ['all'];
        const childrenOf: Record<string, string[]> = {};

        // 1. Channels — register a ch_<channelId> token for each NORMAL channel.
        //    Pseudo rows are addressable via their source entity token below.
        const seenChannelTokens = new Set<string>();
        channels.forEach((c) => {
            if (c.isPseudoChannel) return;
            const realChannelId = c.channelId ?? c.id;
            const token = channelToken(realChannelId);
            if (seenChannelTokens.has(token)) return;
            seenChannelTokens.add(token);
            roots.push(token);
            if (!childrenOf[token]) childrenOf[token] = ['all'];
        });

        // 2. Journals
        journals.forEach((j) => {
            const token = journalToken(j.id);
            roots.push(token);
            if (!childrenOf[token]) childrenOf[token] = ['all'];
        });

        // 3. Pseudo-channel sources — register the source entity as a top-level token.
        pseudoSourceEntityIds.forEach((eid) => {
            const token = entityToken(eid);
            roots.push(token);
            if (!childrenOf[token]) childrenOf[token] = ['all'];
        });

        // 4. Entity hierarchy — include ALL connected entities
        entities.forEach((e) => {
            const entToken = entityToken(e.id);
            if (e.parentId) {
                const pToken = entityToken(e.parentId);
                if (!childrenOf[pToken]) childrenOf[pToken] = ['all'];
                if (!childrenOf[pToken].includes(entToken)) childrenOf[pToken].push(entToken);
            } else {
                const channel = channels.find((c) => !c.isPseudoChannel && (c.channelId ?? c.id) === e.channelId);
                if (channel) {
                    const cToken = channelToken(channel.channelId ?? channel.id);
                    if (childrenOf[cToken]) {
                        if (!childrenOf[cToken].includes(entToken)) childrenOf[cToken].push(entToken);
                    }
                }
            }
        });

        return { roots, childrenOf };
    }, [channels, journals, entities, pseudoSourceEntityIds]);

    /**
     * Build a map from ID tokens to human-readable display names.
     * Used by FormulaEditor for rendering pills.
     */
    const tokenToName = useMemo(() => {
        const map: Record<string, string> = {};

        channels.forEach(c => {
            if (c.isPseudoChannel) {
                // Pseudo-channel rows are addressed in formulas via their source
                // entity token, not via ch_<id>. The source entity is not present
                // in `entities` (the mapper hides it), so register the mapping
                // here using the channel's display name.
                if (c.sourceEntityId) {
                    map[entityToken(c.sourceEntityId)] = c.name;
                }
            } else {
                // Normal channels: tokens use the real Channel.id (c.channelId),
                // which after the architecture change differs from c.id (the
                // BoardChannel row id).
                map[channelToken(c.channelId ?? c.id)] = c.name;
            }
        });

        journals.forEach(j => {
            map[journalToken(j.id)] = j.name;
        });

        entities.forEach(e => {
            map[entityToken(e.id)] = e.name;
        });

        // Categories
        (ALL_CATEGORIES as readonly string[]).forEach(c => {
            map[c] = c.charAt(0).toUpperCase() + c.slice(1);
        });
        map['all'] = 'All';

        return map;
    }, [channels, journals, entities]);

    const [specName, setSpecName] = useState(spec?.name || '');
    const [specType, setSpecType] = useState<'REVENUE' | 'EXPENSE'>(spec?.type || 'EXPENSE');
    const [formula, setFormula] = useState(spec?.formula || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<ValidationError[]>([]);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    // Suggestion state
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionContext, setSuggestionContext] = useState<{
        range: { start: number; end: number };
        type: 'connection' | 'entity' | 'category';
        prefix: string;
    } | null>(null);

    const [cursorPosition, setCursorPosition] = useState(0);

    const [isFocused, setIsFocused] = useState(false);
    const editorRef = useRef<FormulaEditorRef>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const suggestionsListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setSpecName(spec?.name || '');
            setSpecType(spec?.type || 'EXPENSE');
            setFormula(spec?.formula || '');
            setErrors([]);
            setShowSuggestions(false);

        }
    }, [isOpen, spec]);

    // Handle suggestions scrolling
    useEffect(() => {
        if (showSuggestions && suggestionsListRef.current) {
            const list = suggestionsListRef.current;
            const activeItem = list.children[activeSuggestionIndex] as HTMLElement;
            if (activeItem) {
                if (activeItem.offsetTop < list.scrollTop) {
                    list.scrollTop = activeItem.offsetTop;
                } else if (activeItem.offsetTop + activeItem.offsetHeight > list.scrollTop + list.clientHeight) {
                    list.scrollTop = activeItem.offsetTop + activeItem.offsetHeight - list.clientHeight;
                }
            }
        }
    }, [activeSuggestionIndex, showSuggestions]);

    // Update suggestions position after render
    useLayoutEffect(() => {
        if (showSuggestions && editorRef.current) {
            const rect = editorRef.current.getCursorCoordinates();
            if (rect) {
                if (rect.top === 0 && rect.left === 0 && rect.height === 0) return;
                setCoords({ top: rect.top + rect.height, left: rect.left });
            }
        }
    }, [showSuggestions, formula, cursorPosition]);

    const validateDeep = (currentFormula: string, isTyping = false) => {
        const validation = validateFormula(currentFormula, {
            context: validationContext,
            isTyping
        });

        const enrichedParts = validation.parts.map(p => ({
            ...p,
            deepValid: p.isValid,
            deepError: p.error,
            isSoft: p.isSoft
        }));

        return { ...validation, parts: enrichedParts };
    };

    /**
     * Preview data calculation - resolves ID tokens to transaction data
     */
    const previewData = useMemo(() => {
        if (!formula || !finance?.transactions) return null;

        try {
            const cleanFormula = formula.replace(/\s+/g, '');

            // Use AST-based parser instead of regex
            const tokens = parserTokenize(cleanFormula);
            if (tokens.length === 0) return null;

            const context: Record<string, number> = {};
            const matchedTxIds = new Set<string>();

            const getDescendants = (id: string, includeSelf = true): string[] => {
                let ids = includeSelf ? [id] : [];
                entities.filter(e => e.parentId === id).forEach(c => {
                    ids = ids.concat(getDescendants(c.id, true));
                });
                return ids;
            };

            const splitTokenBySegments = (token: string) => {
                const rawParts = token.split('.');
                const segments: string[] = [];
                let currentSegment: string | null = null;

                const isStartOfSegment = (part: string) => {
                    const p = part.toLowerCase();
                    return p.startsWith('ch_') ||
                        p.startsWith('en_') ||
                        p.startsWith('jn_') ||
                        p === 'all' ||
                        p === 'revenues' || p === 'revenue' ||
                        p === 'expenses' || p === 'expense' ||
                        p === 'net';
                };

                rawParts.forEach(part => {
                    if (isStartOfSegment(part)) {
                        if (currentSegment) segments.push(currentSegment);
                        currentSegment = part;
                    } else {
                        if (currentSegment) currentSegment += '.' + part;
                        else currentSegment = part;
                    }
                });
                if (currentSegment) segments.push(currentSegment);
                return segments;
            };

            const resolveToken = (token: string) => {
                const parts = splitTokenBySegments(token);
                parts.pop(); // Remove metric
                const pathParts = parts;

                if (pathParts.length === 0) return [];
                const first = pathParts[0];

                let potentialIds: string[] = [];

                if (isAllKeyword(first)) {
                    potentialIds = [
                        ...entities.filter(e => e.isSelected).map(e => e.id),
                        ...journals.filter(j => j.isEnabled).map(j => `journal-${j.id}`),
                        ...channels.filter(c => (c as any).isEnabled).map(c => `channel-${c.id}`)
                    ];
                } else {
                    const parsed = parseTokenId(first);

                    if (parsed.type === 'journal') {
                        potentialIds = [`journal-${parsed.id}`];

                    } else if (parsed.type === 'channel') {
                        const channelId = parsed.id;

                        // After the architecture change, BoardChannel.id (the row id, used
                        // as `entities[i].channelId` in this flat list) is no longer the
                        // same as Channel.id (the real id used in ch_<id> tokens).
                        // Resolve which BoardChannel row(s) back this real channel id.
                        const boardChannelRowIds = channels
                            .filter((c) => !c.isPseudoChannel && (c.channelId ?? c.id).toLowerCase() === channelId)
                            .map((c) => c.id.toLowerCase());
                        const matchesEntityChannel = (e: any) =>
                            boardChannelRowIds.includes(String(e.channelId || '').toLowerCase());

                        if (pathParts.length === 1) {
                            potentialIds = [
                                `channel-${channelId}`,
                                ...entities.filter(e => matchesEntityChannel(e) && e.isSelected).map(e => e.id)
                            ];
                        } else {
                            let currentLevel = entities.filter(e => matchesEntityChannel(e) && !e.parentId);
                            let currentMatches: string[] = [];

                            for (let i = 1; i < pathParts.length; i++) {
                                const segment = pathParts[i];

                                if (isAllKeyword(segment)) {
                                    const descendantIds = currentLevel.flatMap(e => getDescendants(e.id, true)).filter(id => {
                                        const ent = entities.find(ae => ae.id === id);
                                        return ent?.isSelected;
                                    });
                                    if (i === 1) {
                                        return [`channel-${channelId}`, ...descendantIds];
                                    }
                                    return descendantIds;
                                }

                                const segParsed = parseTokenId(segment);
                                if (segParsed.type === 'entity') {
                                    let match = currentLevel.find(e => e.id.toLowerCase() === segParsed.id);

                                    if (!match && i === 1) {
                                        const deepMatch = entities.find(e =>
                                            e.id.toLowerCase() === segParsed.id &&
                                            matchesEntityChannel(e)
                                        );
                                        if (deepMatch) match = deepMatch;
                                    }

                                    if (match) {
                                        currentLevel = entities.filter(e => e.parentId === match.id);
                                        currentMatches = [match.id];
                                    } else return null;
                                } else return null;
                            }
                            potentialIds = currentMatches.flatMap(id => getDescendants(id, true));
                        }

                    } else if (parsed.type === 'entity') {
                        const rootEntity = entities.find(e => e.id.toLowerCase() === parsed.id);

                        // Pseudo-channel source entities are hidden from `entities`
                        // (the mapper scopes the tree to descendants only). Fall
                        // back to the matching pseudo channel's sourceEntityId as
                        // a virtual root — descendants still carry the DB parentId.
                        let rootId: string | null = rootEntity?.id ?? null;
                        if (!rootId) {
                            const matchingPseudo = channels.find((c) =>
                                c.isPseudoChannel &&
                                (c.sourceEntityId || '').toLowerCase() === parsed.id
                            );
                            if (matchingPseudo?.sourceEntityId) {
                                rootId = matchingPseudo.sourceEntityId;
                            }
                        }

                        if (rootId) {
                            if (pathParts.length === 1) {
                                potentialIds = [rootId];
                            } else {
                                let currentLevel = entities.filter(e => e.parentId === rootId);
                                let currentMatches = [rootId];

                                for (let i = 1; i < pathParts.length; i++) {
                                    const segment = pathParts[i];

                                    if (isAllKeyword(segment)) {
                                        return currentMatches.flatMap(id => getDescendants(id, false)).filter(id => {
                                            const ent = entities.find(ae => ae.id === id);
                                            return ent?.isSelected;
                                        });
                                    }

                                    const segParsed = parseTokenId(segment);
                                    if (segParsed.type === 'entity') {
                                        const match = currentLevel.find(e => e.id.toLowerCase() === segParsed.id);
                                        if (match) {
                                            currentLevel = entities.filter(e => e.parentId === match.id);
                                            currentMatches = [match.id];
                                        } else return null;
                                    } else return null;
                                }
                                potentialIds = currentMatches.flatMap(id => getDescendants(id, true));
                            }
                        }
                    }
                }

                if (!potentialIds) return null;

                const finalIds = potentialIds.filter(id => {
                    if (id.startsWith('journal-')) return true;
                    if (id.startsWith('channel-')) return true;
                    const ent = entities.find(e => e.id === id);
                    return ent?.isSelected;
                });

                return finalIds.length > 0 ? finalIds : null;
            };

            tokens.forEach(token => {
                const lowerToken = token.toLowerCase();
                const eIds = resolveToken(lowerToken);
                if (!eIds) {
                    context[lowerToken] = NaN;
                    return;
                }

                let rev = 0, exp = 0;

                finance?.transactions?.forEach((t: any) => {
                    if (t.source === 'spec' || t.category === 'Calculated' || String(t.entityId).startsWith('spec-')) return;

                    const tEid = t.entityId ? String(t.entityId) : null;
                    const tCid = t.channelId ? String(t.channelId) : null;
                    const tOid = t.originalEntityId ? String(t.originalEntityId) : tEid;

                    let isMatch = false;

                    if (tEid && !tEid.startsWith('journal-')) {
                        isMatch = eIds.includes(tEid) || (tOid ? eIds.includes(tOid) : false) || (tCid ? eIds.includes(`channel-${tCid}`) : false);
                    } else if (tEid && tEid.startsWith('journal-')) {
                        isMatch = eIds.includes(tEid) || (tOid ? eIds.includes(tOid) : false);
                    } else if (tCid) {
                        isMatch = eIds.includes(`channel-${tCid}`);
                    }

                    if (!isMatch) return;

                    const amount = Number(t.convertedAmount || t.amount || 0);
                    const type = (t.type || t.transactionType || '').toUpperCase();
                    if (type === 'REVENUE') {
                        rev += amount;
                        matchedTxIds.add(t.id);
                    } else if (type === 'EXPENSE') {
                        exp += amount;
                        matchedTxIds.add(t.id);
                    }
                });

                if (lowerToken.endsWith('.revenues') || lowerToken.endsWith('.revenue')) context[lowerToken] = rev;
                else if (lowerToken.endsWith('.expenses') || lowerToken.endsWith('.expense')) context[lowerToken] = exp;
                else if (lowerToken.endsWith('.net')) context[lowerToken] = rev - exp;
            });

            const value = parserEvaluate(cleanFormula, context);
            const finalValue = typeof value === 'number' && isFinite(value) ? value : NaN;
            return {
                value: finalValue,
                isExpense: specType === 'EXPENSE',
                txCount: matchedTxIds.size
            };
        } catch (e) {
            return null;
        }
    }, [formula, finance, entities, channels, journals, specType]);

    const previewValue = (previewData && !isNaN(previewData.value)) ? previewData.value : null;

    const handleSave = async () => {
        const formulaToSave = formula.replace(/\s+/g, '');
        const validation = validateDeep(formulaToSave);
        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        if (!specName.trim()) {
            setErrors([{ key: 'boardSpecs.specNameRequired' }]);
            return;
        }

        setIsSubmitting(true);
        try {
            const data = {
                name: specName,
                formula: formulaToSave,
                type: specType
            };
            let response;
            if (spec) {
                response = await boardService.updateBoardSpec(boardId, spec.id, data);
            } else {
                response = await boardService.createBoardSpec(boardId, data);
            }

            if (response.success) {
                const savedSpec = response.data;
                // Use the client-side previewValue as the optimistic value so the
                // content modal shows the correct number immediately. The background
                // refetch will replace it with the server-calculated value.
                const updatedSpecs = spec
                    ? specs.map((s: any) => s.id === spec.id ? { ...s, ...savedSpec, value: previewValue } : s)
                    : [...specs, { ...savedSpec, value: previewValue }];

                setSpecs(updatedSpecs);
                queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });

                onClose();
            } else {
                setErrors([{ key: 'boardSpecs.failedToSave' }]);
            }
        } catch (error) {
            console.error('Failed to save spec', error);
            setErrors([{ key: 'boardSpecs.failedToSave' }]);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Smart Input & Suggestions Logic ---

    const updateSuggestions = (value: string, cursorPosition: number) => {
        const lastBoundary = Math.max(
            value.lastIndexOf('+', cursorPosition - 1),
            value.lastIndexOf('-', cursorPosition - 1),
            value.lastIndexOf('*', cursorPosition - 1),
            value.lastIndexOf('/', cursorPosition - 1),
            value.lastIndexOf('(', cursorPosition - 1),
            value.lastIndexOf(')', cursorPosition - 1)
        );

        const tokenStart = lastBoundary + 1;
        const currentToken = value.substring(tokenStart, cursorPosition);

        if (value[cursorPosition - 1] === ' ' || (!currentToken.trim() && value[cursorPosition - 1] !== '.' && value.length > 0 && !['+', '-', '*', '/', '(', ')'].includes(value[cursorPosition - 1]))) {
            setShowSuggestions(false);
            return;
        }

        const segments = currentToken.split('.');
        const lastSegment = segments[segments.length - 1].toLowerCase().trim();

        if (segments.length > 1) {
            const prevSegLower = segments[segments.length - 2].toLowerCase();
            if (isCategory(prevSegLower)) {
                setShowSuggestions(false);
                return;
            }
        }

        let newSuggestions: any[] = [];
        let type: 'connection' | 'entity' | 'category' = 'connection';

        const getTopLevelSuggestions = () => {
            const normalChannels: any[] = [];
            const pseudoEntries: any[] = [];

            const seenChannelIds = new Set<string>();
            channels.forEach((c) => {
                if (c.isPseudoChannel && c.sourceEntityId) {
                    const sourceEntity = entities.find((e) => e.id === c.sourceEntityId);
                    pseudoEntries.push({
                        id: c.sourceEntityId,
                        token: entityToken(c.sourceEntityId),
                        name: sourceEntity?.name || c.name,
                        type: 'entity',
                        connection: c,
                    });
                } else {
                    const realChannelId = c.channelId ?? c.id;
                    if (seenChannelIds.has(realChannelId)) return;
                    seenChannelIds.add(realChannelId);
                    normalChannels.push({
                        id: realChannelId,
                        token: channelToken(realChannelId),
                        name: c.name,
                        type: 'channel',
                        connection: c,
                    });
                }
            });

            const allJournals = journals.map((j) => ({
                id: j.id,
                token: journalToken(j.id),
                name: j.name,
                type: 'journal',
                connection: j,
            }));

            return [
                { id: 'all', token: 'all', name: 'all', type: 'special' },
                ...normalChannels,
                ...pseudoEntries,
                ...allJournals,
            ];
        };

        if (!currentToken.trim()) {
            const connSuggestions = getTopLevelSuggestions();
            if (connSuggestions.length > 0) {
                newSuggestions = connSuggestions;
                type = 'connection';
            }
        } else if (segments.length === 1) {
            const connSuggestions = getTopLevelSuggestions().filter(s =>
                s.name.toLowerCase().includes(lastSegment)
            );
            newSuggestions = connSuggestions;
            type = 'connection';
        } else {
            const rootToken = segments[0];

            if (isAllKeyword(rootToken)) {
                newSuggestions = CATEGORIES.filter(c => c.includes(lastSegment))
                    .map(c => ({ id: c, token: c, name: c, type: 'category' }));
                type = 'category';
            } else {
                const rootParsed = parseTokenId(rootToken);
                let connection: any = null;
                let rootEntityId: string | undefined;

                if (rootParsed.type === 'channel') {
                    connection = channels.find((c) => !c.isPseudoChannel && (c.channelId ?? c.id) === rootParsed.id);
                } else if (rootParsed.type === 'journal') {
                    connection = journals.find((j) => j.id === rootParsed.id);
                } else if (rootParsed.type === 'entity') {
                    const matchingPseudo = channels.find((c) => c.isPseudoChannel && c.sourceEntityId === rootParsed.id);
                    if (matchingPseudo) {
                        connection = matchingPseudo;
                        rootEntityId = matchingPseudo.sourceEntityId ?? undefined;
                    }
                }

                if (connection) {
                    const entityPath = segments.slice(1, -1);
                    const isJournal = rootParsed.type === 'journal';

                    if (entityPath.length === 0) {
                        let children: any[] = [];
                        if (rootEntityId) {
                            children = entities.filter(e => e.channelId === connection!.id && e.parentId === rootEntityId);
                        } else if (!isJournal) {
                            children = entities.filter(e => e.channelId === connection!.id && !e.parentId);
                        }

                        newSuggestions = [];

                        if (children.length > 0) {
                            const isEntityActive = (eId: string): boolean => {
                                const ent = entities.find(e => e.id === eId);
                                if (!ent) return false;
                                if (ent.isSelected) return true;
                                return entities.some(other => other.parentId === eId && isEntityActive(other.id));
                            };

                            newSuggestions.push({
                                id: 'all',
                                token: 'all',
                                name: 'All',
                                type: 'special',
                                connection
                            });

                            newSuggestions.push(...children.filter(e => isEntityActive(e.id)).map(e => ({
                                id: e.id,
                                token: entityToken(e.id),
                                name: e.name,
                                type: 'entity',
                                connection
                            })));
                        }

                        newSuggestions = newSuggestions.filter(s => s.name.toLowerCase().includes(lastSegment));

                        if (newSuggestions.length === 0 && children.length === 0) {
                            if (rootEntityId) {
                                // Pseudo source entity with no loaded children: route through
                                // 'all' so the produced formula is en_<id>.all.<category>,
                                // which the validator accepts (en_<id>.<category> is rejected
                                // since pseudo source roots aren't journal roots).
                                newSuggestions = [{
                                    id: 'all', token: 'all', name: 'All', type: 'special', connection,
                                }].filter(s => s.name.toLowerCase().includes(lastSegment));
                                type = 'entity';
                            } else {
                                newSuggestions = CATEGORIES.filter(c => c.includes(lastSegment))
                                    .map(c => ({ id: c, token: c, name: c, type: 'category', connection }));
                                type = 'category';
                            }
                        }

                        type = newSuggestions.some(s => s.type === 'category') ? 'category' : 'entity';

                    } else if (isAllKeyword(entityPath[0])) {
                        newSuggestions = CATEGORIES.filter(c => c.includes(lastSegment))
                            .map(c => ({ id: c, token: c, name: c, type: 'category', connection }));
                        type = 'category';

                    } else {
                        let parentId = rootEntityId || undefined;

                        let validPath = true;
                        for (const segmentToken of entityPath) {
                            if (isAllKeyword(segmentToken)) {
                                validPath = false;
                                break;
                            }

                            const segParsed = parseTokenId(segmentToken);
                            if (segParsed.type === 'entity') {
                                const nextEntity = entities.find(e =>
                                    e.channelId === connection!.id &&
                                    e.id === segParsed.id &&
                                    (parentId ? e.parentId === parentId : !e.parentId)
                                );

                                if (nextEntity) {
                                    parentId = nextEntity.id;
                                } else {
                                    validPath = false;
                                    break;
                                }
                            } else {
                                validPath = false;
                                break;
                            }
                        }

                        if (validPath) {
                            const isEntityActive = (eId: string): boolean => {
                                const ent = entities.find(e => e.id === eId);
                                if (!ent) return false;
                                if (ent.isSelected) return true;
                                return entities.some(other => other.parentId === eId && isEntityActive(other.id));
                            };

                            const children = entities.filter(e => e.parentId === parentId && isEntityActive(e.id));
                            const hasChildren = entities.some(e => e.parentId === parentId);

                            newSuggestions = [
                                ...(hasChildren ? [{
                                    id: 'all',
                                    token: 'all',
                                    name: 'All',
                                    type: 'special',
                                    connection
                                }] : []),
                                ...children.map(e => ({
                                    id: e.id,
                                    token: entityToken(e.id),
                                    name: e.name,
                                    type: 'entity',
                                    connection
                                })),
                                ...(children.length === 0 ? CATEGORIES.map(c => ({
                                    id: c, token: c, name: c, type: 'category', connection
                                })) : [])
                            ].filter(s => s.name.toLowerCase().includes(lastSegment));

                            type = 'entity';
                        } else {
                            const lastRealSegment = entityPath[entityPath.length - 1];
                            if (isAllKeyword(lastRealSegment)) {
                                newSuggestions = CATEGORIES.filter(c => c.includes(lastSegment))
                                    .map(c => ({ id: c, token: c, name: c, type: 'category', connection }));
                                type = 'category';
                            }
                        }
                    }
                }
            }
        }

        if (newSuggestions.length > 0) {
            if (newSuggestions.length === 1 && newSuggestions[0].token === lastSegment && value[cursorPosition - 1] !== '.' && value[cursorPosition - 1] !== ' ') {
                setShowSuggestions(false);
                return;
            }

            setSuggestions(newSuggestions);
            setSuggestionContext({ range: { start: tokenStart, end: cursorPosition }, type, prefix: lastSegment });

            setShowSuggestions(true);
            setActiveSuggestionIndex(0);
        } else {
            setShowSuggestions(false);
        }
    };

    /**
     * When a suggestion is clicked, insert its ID token into the formula.
     * The FormulaEditor will display the human-readable name via tokenToName.
     */
    const handleSuggestionClick = (suggestion: any) => {
        if (!suggestionContext) return;

        const tokenToInsert = suggestion.token;
        const before = formula.substring(0, suggestionContext.range.start);

        const currentToken = formula.substring(suggestionContext.range.start, suggestionContext.range.end);
        const segments = currentToken.split('.');
        segments[segments.length - 1] = tokenToInsert;

        let newToken = segments.join('.').replace(/\s+/g, '');

        const isTerminal = isCategory(tokenToInsert);
        if (!isTerminal) {
            newToken += '.';
        }

        const after = formula.substring(suggestionContext.range.end);
        const nextFormula = (before + newToken + after).replace(/\s+/g, '');

        setFormula(nextFormula);
        setShowSuggestions(false);

        setTimeout(() => {
            if (editorRef.current) {
                const newPos = before.length + newToken.length;
                editorRef.current.focus(newPos);

                if (!isTerminal) {
                    updateSuggestions(nextFormula, newPos);
                }
            }
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveSuggestionIndex(prev => (prev + 1) % suggestions.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveSuggestionIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleSuggestionClick(suggestions[activeSuggestionIndex]);
            } else if (e.key === 'Escape') {
                setShowSuggestions(false);
            }
        }
    };

    const renderSuggestionIcon = (suggestion: any) => {
        const { type, name, connection } = suggestion;
        const lowerName = name.toLowerCase();

        if (lowerName.includes('revenu')) {
            return (
                <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_0deebbb1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_0deebbb1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </div>
            );
        } else if (lowerName.includes('expense')) {
            return (
                <div className="w-5 h-5 rounded flex items-center justify-center bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_0b286e67.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_0b286e67.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </div>
            );
        } else if (lowerName.includes('net')) {
            return (
                <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_52559d1c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_52559d1c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </div>
            );
        } else if (lowerName === 'all') {
            return (
                <div className="w-5 h-5 rounded flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_32485592.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_32485592.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                </div>
            );
        }

        if (type === 'channel' || type === 'journal' || type === 'entity' || type === 'category') {
            const activeConnection = connection || [...channels, ...journals].find(c => c.name === name);

            if (activeConnection) {
                const iconUrl = (activeConnection as any).icon || (activeConnection as any).channelTypeConfig?.icon;

                if (iconUrl && (iconUrl.startsWith('http') || iconUrl.startsWith('/'))) {
                    return (
                        <div className="w-5 h-5 rounded flex items-center justify-center bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 p-0.5">
                            <img src={iconUrl} alt={name} className="w-full h-full object-contain" />
                        </div>
                    );
                }
            }

            if (type === 'channel') {
                return (
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                        <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_da3c3fe6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_da3c3fe6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                );
            } else {
                return (
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                        <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_04d22853.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_04d22853.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                );
            }
        }

        return (
            <div className="w-5 h-5 rounded flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_062452dc.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_062452dc.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </div>
        );
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose} initialFocus={nameInputRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl transition-all flex flex-col max-h-[90vh] border border-transparent dark:border-neutral-800">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 relative">
                                <div className="flex items-center gap-4">
                                    {onBack && (
                                        <button
                                            onClick={onBack}
                                            className="text-neutral-400 hover:text-accent-primary transition-colors"
                                        >
                                            <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_21b3aba3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_21b3aba3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    )}
                                    <Dialog.Title className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                                        {spec ? t('boardSpecs.editTitle', 'Edit Board Spec') : t('boardSpecs.addTitle', 'Add Board Spec')}
                                    </Dialog.Title>
                                </div>
                            </div>

                            <div className="p-6 overflow-y-auto flex-1">
                                <div className="space-y-6">
                                    <div className="flex items-end gap-3 flex-nowrap">
                                        <div className="flex-1">
                                            <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-2">{t('boardSpecs.specNameLabel', 'Spec Name')}</label>
                                            <input
                                                ref={nameInputRef}
                                                type="text"
                                                name="board-spec-title-input"
                                                autoComplete="off"
                                                data-1p-ignore
                                                value={specName}
                                                onChange={(e) => setSpecName(e.target.value)}
                                                placeholder={String(t('boardSpecs.specNamePlaceholder', 'e.g. Net Profit Margin'))}
                                                className="w-full px-4 py-2.5 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:border-accent-primary transition-colors text-sm text-neutral-900 dark:text-neutral-100 h-11"
                                            />
                                        </div>
                                        <div className="flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setSpecType(specType === 'REVENUE' ? 'EXPENSE' : 'REVENUE')}
                                                className={`w-[80px] h-11 flex items-center justify-center gap-1.5 px-3 rounded-xl transition-all duration-200 whitespace-nowrap ${specType === 'REVENUE'
                                                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-900/30 hover:bg-emerald-100/60 dark:hover:bg-emerald-800/50 shadow-sm border border-emerald-100/50 dark:border-emerald-800/50'
                                                    : 'text-red-600 dark:text-red-400 bg-red-50/40 dark:bg-red-900/30 hover:bg-red-100/60 dark:hover:bg-red-800/50 shadow-sm border border-red-100/50 dark:border-red-800/50'
                                                    }`}
                                            >
                                                {specType === 'REVENUE' ? (
                                                    <span className="w-3 h-3 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17e64163.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                ) : (
                                                    <span className="w-3 h-3 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d1b707dd.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                )}
                                                <span className="text-[9px] font-black uppercase tracking-tighter">
                                                    {specType === 'REVENUE' ? t('common.revenue') : t('common.expense')}
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-bold text-neutral-700 dark:text-neutral-300">
                                                {t('boardSpecs.formulaLabel', 'Formula')}
                                            </label>
                                            {previewValue !== null && (
                                                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                                    {t('boardSpecs.estimatedValue', 'estimated value')} : <span className={`font-mono font-bold ${specType === 'REVENUE'
                                                        ? 'text-emerald-500'
                                                        : 'text-rose-500'
                                                        }`}>
                                                        {previewValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currencySettings?.default === 'USD' ? '$' : (currencySettings?.default || '')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="relative">
                                            {/* Formula Editor */}
                                            <FormulaEditor
                                                ref={editorRef}
                                                value={formula}
                                                onChange={(val, cursor) => {
                                                    let newVal = val;
                                                    let newCursor = cursor;

                                                    if (val[cursor - 1] === ' ') {
                                                        const beforeSpace = val.substring(0, cursor - 1);
                                                        const afterSpace = val.substring(cursor);

                                                        if (beforeSpace.endsWith(' ') || beforeSpace.endsWith('.') || ['+', '-', '*', '/', '(', ')'].includes(beforeSpace.trim().slice(-1)) || afterSpace.startsWith('.')) {
                                                            newVal = beforeSpace + afterSpace;
                                                            newCursor = cursor - 1;
                                                        }
                                                    }

                                                    setFormula(newVal);
                                                    setCursorPosition(newCursor);
                                                    updateSuggestions(newVal, newCursor);
                                                }}
                                                tokenToName={tokenToName}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !showSuggestions) {
                                                        e.preventDefault();
                                                        handleSave();
                                                    } else {
                                                        handleKeyDown(e);
                                                    }
                                                }}
                                                onFocus={() => {
                                                    setIsFocused(true);
                                                    if (!formula) {
                                                        setCursorPosition(0);
                                                        requestAnimationFrame(() => {
                                                            // We check the ref or just trust the state will be checked in the render gate
                                                            updateSuggestions('', 0);
                                                        });
                                                    }
                                                }}
                                                onBlur={() => {
                                                    setIsFocused(false);
                                                    setShowSuggestions(false);
                                                }}
                                                cursorIndex={cursorPosition}
                                            />

                                            {/* Suggestions Dropdown */}
                                            {isFocused && showSuggestions && createPortal(
                                                <div
                                                    ref={suggestionsListRef}
                                                    className="fixed w-72 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-xl z-[9999] overflow-hidden font-mono text-sm max-h-60 overflow-y-auto"
                                                    style={{
                                                        top: coords.top,
                                                        left: coords.left,
                                                    }}
                                                >
                                                    {suggestions.map((s, i) => (
                                                        <button
                                                            key={s.token || s.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSuggestionClick(s);
                                                            }}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            className={`w-full px-2 py-1 text-left flex items-center justify-between cursor-pointer ${i === activeSuggestionIndex ? 'bg-[#0060C0] text-white' : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200'}`}
                                                        >
                                                            <div className="flex items-center gap-2 overflow-hidden">
                                                                <div className="flex-shrink-0">
                                                                    {renderSuggestionIcon(s)}
                                                                </div>
                                                                <span className="truncate font-medium">{s.name}</span>
                                                            </div>
                                                            <span className={`text-[10px] uppercase tracking-wider flex-shrink-0 ${i === activeSuggestionIndex ? 'text-white/70' : 'text-neutral-400 dark:text-neutral-500'}`}>
                                                                {s.connection && s.type !== 'category' && s.type !== 'special' ? (
                                                                    s.connection.channelType
                                                                        ? String(t(`channels.${s.connection.channelType}`, s.connection.channelType)).toUpperCase()
                                                                        : String(t('boardSpecs.types.journal', 'Journal')).toUpperCase()
                                                                ) : ''}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>,
                                                document.body
                                            )}
                                        </div>

                                        {/* Quick Operator Buttons */}
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {OPERATORS.map(op => (
                                                <button
                                                    key={op}
                                                    onClick={() => {
                                                        const pos = cursorPosition || formula.length;
                                                        const newFormula = formula.substring(0, pos) + op + formula.substring(pos);
                                                        setFormula(newFormula);
                                                        setTimeout(() => {
                                                            if (editorRef.current) {
                                                                editorRef.current.focus(pos + 1);
                                                            }
                                                        }, 0);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:border-accent-primary dark:hover:border-accent-primary hover:text-accent-primary transition-all shadow-sm"
                                                >
                                                    {op}
                                                </button>
                                            ))}
                                            <div className="w-px h-8 bg-neutral-200 dark:bg-neutral-700 mx-1" />
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0, '.'].map(num => (
                                                <button
                                                    key={num}
                                                    onClick={() => {
                                                        const pos = cursorPosition || formula.length;
                                                        setFormula(formula.substring(0, pos) + num + formula.substring(pos));
                                                        setTimeout(() => {
                                                            if (editorRef.current) {
                                                                editorRef.current.focus(pos + 1);
                                                            }
                                                        }, 0);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm font-medium text-neutral-400 dark:text-neutral-500 hover:border-accent-primary dark:hover:border-accent-primary hover:text-accent-primary transition-all shadow-sm"
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Error Messages */}
                                    {errors.length > 0 && (
                                        <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800/30 rounded-xl">
                                            <div className="text-xs font-bold text-rose-500 dark:text-rose-400 uppercase mb-2">{t('boardSpecs.errorsTitle', 'Errors')}</div>
                                            <ul className="text-sm text-rose-600 dark:text-rose-300 space-y-1">
                                                {errors.map((err, i) => <li key={i}>• {String(t(err.key, err.params || {}))}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-6 py-4 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-100 dark:border-neutral-800 flex flex-col gap-4 rounded-b-2xl">
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={onClose}
                                        className="px-6 py-2.5 text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                                    >
                                        {t('boardSpecs.cancel', 'Cancel')}
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSubmitting}
                                        className="px-8 py-2.5 bg-accent-primary hover:bg-accent-primary/90 text-white rounded-xl text-sm font-bold shadow-lg shadow-accent-primary/20 transition-all transform active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        {isSubmitting ? t('boardSpecs.saving', 'Saving...') : t('boardSpecs.save', 'Save Spec')}
                                    </button>
                                </div>
                            </div>
                        </Dialog.Panel >
                    </div >
                </div >
            </Dialog >
        </Transition >
    );
};
